import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { Navbar } from "../components/Navbar";
import { api } from "../lib/api";
import { Search, ArrowRight, MessageCircle, ShieldCheck, Loader2 } from "lucide-react";

const FALLBACK = "https://images.unsplash.com/photo-1560518883-ce09059eeffa?w=800&q=80";
const propPhoto = (p) => p.has_photo ? `${process.env.REACT_APP_BACKEND_URL}/api/properties/${p.slug}/photo` : (p.photo_external || FALLBACK);

export default function SearchResults() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const q = params.get("q") || "";
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/properties/search?q=${encodeURIComponent(q)}`)
      .then((r) => setResults(r.data)).catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, [q]);

  return (
    <div className="App bg-[#F4F7FA] min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-5 py-10" data-testid="search-page">
        <h1 className="font-heading font-extrabold text-3xl text-navy">
          {loading ? "Searching…" : `${results.length} result${results.length === 1 ? "" : "s"} for “${q}”`}
        </h1>
        <p className="mt-1 text-slate-500">Properties matching your search across the UK.</p>

        {loading ? (
          <div className="mt-16 flex justify-center" data-testid="search-loading"><Loader2 className="w-8 h-8 text-mint animate-spin" /></div>
        ) : results.length === 0 ? (
          <div className="mt-10 bg-white border border-slate-200 rounded-2xl p-10 text-center" data-testid="search-empty">
            <p className="text-slate-600">No properties found for “{q}”.</p>
            <button onClick={() => navigate("/write-review")} className="mt-4 bg-mint text-white font-semibold px-5 py-2.5 rounded-xl hover:bg-mint-dark transition-colors">Be the first to review a property</button>
          </div>
        ) : (
          <div className="mt-8 grid md:grid-cols-3 gap-6" data-testid="search-grid">
            {results.map((p) => (
              <button key={p.id} data-testid={`search-result-${p.slug}`} onClick={() => navigate(`/address/${p.slug}`)}
                className="text-left bg-white rounded-2xl border border-slate-200 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-1 transition-all">
                <div className="h-44 overflow-hidden">
                  <img src={propPhoto(p)} alt={p.address_line} onError={(e) => { e.currentTarget.src = FALLBACK; }} className="w-full h-full object-cover" />
                </div>
                <div className="p-5">
                  <h3 className="font-heading font-bold text-lg text-navy leading-tight">{p.address_line}</h3>
                  <p className="text-mint-dark font-medium text-sm">{p.city} · {p.postcode}</p>
                  {p.subscription_active && (
                    <span className="mt-2 inline-flex items-center gap-1 bg-mint-soft text-mint-dark px-2 py-0.5 rounded-md text-xs font-semibold">
                      <ShieldCheck className="w-3.5 h-3.5" /> Verified Landlord
                    </span>
                  )}
                  <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-sm text-slate-500">
                    <span className="flex items-center gap-1.5"><MessageCircle className="w-4 h-4" /> {p.aggregates?.count ?? 0} reviews</span>
                    <span className="flex items-center gap-1 text-mint-dark font-semibold">View <ArrowRight className="w-4 h-4" /></span>
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
